// Peer recognition (Phase 5a) — give kudos, a recognition wall, leaderboard and
// your own points. Shared by employees and HR.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Award, Trophy, Heart } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const CATS = ['Teamwork', 'Innovation', 'Excellence', 'Leadership', 'Customer'];
const initials = (n) => (n || '').split(' ').map((x) => x[0]).slice(0, 2).join('').toUpperCase();

export default function Recognition({ showToast }) {
  const [wall, setWall] = useState(null);
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.recognitionWall().then(setWall).catch(() => setWall([]));
    api.recognitionMe().then(setMe).catch(() => setMe(null));
    api.recognitionLeaderboard().then(setBoard).catch(() => setBoard([]));
  };
  useEffect(() => { load(); api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([])); }, []);

  const give = async () => {
    if (!form.toId || !form.message.trim() || busy) return; setBusy(true);
    try { await api.giveRecognition(form); showToast?.('Recognition sent 🎉'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!wall) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Recognition" sub="Celebrate great work across the team" />
        <PrimaryBtn onClick={() => setForm({ toId: '', points: 10, category: 'Teamwork', message: '' })}><Award size={14} style={{ marginRight: 6 }} /> Give recognition</PrimaryBtn>
      </div>

      {me && (
        <div className="grid grid-cols-3 gap-3">
          {[['My points', me.points], ['Received', me.receivedCount], ['Given', me.givenCount]].map(([l, v]) => (
            <Card key={l} style={{ padding: 16 }}><div style={{ fontSize: 22, fontWeight: 700, color: AG.text }}>{v}</div><div style={{ fontSize: 12, color: AG.n500, marginTop: 3 }}>{l}</div></Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Wall */}
        <div className="lg:col-span-2 space-y-3">
          {wall.length === 0 ? (
            <Card><EmptyState icon={Heart} title="No recognitions yet" desc="Be the first to recognize a teammate." /></Card>
          ) : wall.map((r) => (
            <Card key={r.id} style={{ padding: 16 }}>
              <div className="flex items-start gap-3">
                <div style={{ width: 38, height: 38, borderRadius: 10, background: AG.primaryLightest, color: AG.primary, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(r.fromName)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: AG.text }}><b>{r.fromName}</b> recognized <b>{r.toName}</b></div>
                  <div style={{ fontSize: 13, color: AG.n600, margin: '4px 0' }}>{r.message}</div>
                  <div className="flex items-center gap-2"><Pill tone="info">{r.category}</Pill><Pill tone="success">+{r.points}</Pill><span style={{ fontSize: 11, color: AG.n400 }}>{r.createdAt?.slice(0, 10)}</span></div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Leaderboard */}
        <Card style={{ padding: 18, alignSelf: 'flex-start' }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 12 }}><Trophy size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Leaderboard</span></div>
          {board.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No points yet.</p> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {board.map((b, i) => (
                <div key={b.id} className="flex items-center justify-between">
                  <span style={{ fontSize: 13.5, color: AG.text }}><b style={{ color: AG.n400, marginRight: 6 }}>{i + 1}</b>{b.name}</span>
                  <span style={{ fontWeight: 700, color: AG.primary }}>{b.points}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Give recognition</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>To</div>
              <select value={form.toId} onChange={(e) => setForm({ ...form, toId: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>
                <option value="">Select a teammate…</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.dept ? ` · ${p.dept}` : ''}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Category</div>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Points</div>
                  <select value={form.points} onChange={(e) => setForm({ ...form, points: Number(e.target.value) })} className="ag-input" style={{ marginTop: 4 }}>{[0, 5, 10, 20, 30, 50].map((p) => <option key={p} value={p}>{p}</option>)}</select>
                </div>
              </div>
              <TextField fullWidth size="small" label="Message" multiline minRows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={give} disabled={busy || !form?.toId || !form?.message?.trim()}>Send</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
